import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Paperclip, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import API from '@/lib/api';
import { buildClientFeedbackTelemetry } from '@/lib/feedbackTelemetry';
import { useAuth } from '@/lib/auth';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_MIMES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
];
const ATTACHMENT_ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp';

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} kB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const FeedbackModal = ({
  open,
  onOpenChange,
  screenshotBlob,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  screenshotBlob: Blob | null;
}) => {
  const { t } = useTranslation();
  const { user } = useAuth({
    tmp: 'FeedbackModal',
  });
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addAttachments = (fileList: FileList | null) => {
    if (!fileList) return;
    const incoming = Array.from(fileList);
    setAttachments((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        if (next.length >= MAX_ATTACHMENTS) {
          toast.error(t('feedbackModal.errors.maxAttachments', { max: MAX_ATTACHMENTS }));
          break;
        }
        if (!ALLOWED_ATTACHMENT_MIMES.includes(file.type)) {
          toast.error(
            t('feedbackModal.errors.unsupportedType', { fileName: file.name }),
          );
          continue;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          toast.error(t('feedbackModal.errors.fileTooLarge', { fileName: file.name }));
          continue;
        }
        next.push(file);
      }
      return next;
    });
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const onClickSend = () => {
    const formData = new FormData();
    formData.append('message', message);
    formData.append(
      'telemetry',
      JSON.stringify(buildClientFeedbackTelemetry()),
    );
    if (!user?.email && email.trim()) {
      formData.append('contactEmail', email.trim());
    }

    const send = async () => {
      if (screenshotBlob && screenshotBlob.size > 0) {
        formData.append('screenshot', screenshotBlob, 'feedback.jpg');
      }
      for (const file of attachments) {
        formData.append('attachments', file, file.name);
      }
      return API.post('/feedback', formData);
    };

    void send()
      .then(() => {
        toast.success(t('feedbackModal.success'), {
          duration: 5000,
          dismissible: true,
        });
        onOpenChange(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('feedbackModal.errors.sendFailed.title'), {
          description: t('feedbackModal.errors.sendFailed.description'),
          duration: 5000,
          dismissible: true,
        });
      });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setMessage('');
      setEmail('');
      setAttachments([]);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="min-w-[90vw] md:min-w-[40vw]">
        <DialogHeader>
          <DialogTitle>{t('feedbackModal.title')}</DialogTitle>
          <DialogDescription>
            {t('feedbackModal.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogDescription className="flex flex-col gap-4">
          <Textarea
            className="h-40"
            placeholder={t('feedbackModal.messagePlaceholder')}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
            }}
          />

          {!user?.email && (
            <Input
              type="email"
              placeholder={t('feedbackModal.emailPlaceholder')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          )}

          <div className="flex flex-col gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              onChange={(e) => {
                addAttachments(e.target.files);
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="w-fit"
              disabled={attachments.length >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-4 w-4 mr-2" />
              {t('feedbackModal.attachFile')}
            </Button>
            <span className="text-xs text-muted-foreground">
              {t('feedbackModal.attachmentHint', { max: MAX_ATTACHMENTS })}
            </span>
            {attachments.length > 0 && (
              <ul className="flex flex-col gap-1">
                {attachments.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-2 rounded-md border px-2 py-1 text-sm"
                  >
                    <span className="truncate" title={file.name}>
                      {file.name}{' '}
                      <span className="text-muted-foreground">
                        ({formatFileSize(file.size)})
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      aria-label={t('feedbackModal.removeAttachment')}
                      onClick={() => removeAttachment(index)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Button onClick={onClickSend}>{t('feedbackModal.submit')}</Button>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
};
